import { useState } from "react";
import { Pool } from "@/types";
import { HtmlTextEditor } from "../components/ui/HtmlTextEditor";

function AddPoolForm({
  onSubmit,
  onCancel,
  initialPool,
  loading = false,
}: {
  onSubmit: (pool: Pool) => void;
  onCancel: () => void;
  initialPool?: Pool | null;
  loading?: boolean;
}) {
  const [poolname, setPoolname] = useState(initialPool?.poolname ?? "");
  const [city, setCity] = useState(initialPool?.city ?? "");
  const [state, setState] = useState(initialPool?.state ?? "");
  const [type, setType] = useState(initialPool?.type ?? "");
  const [timing, setTiming] = useState(initialPool?.timing ?? "");
  const [about, setAbout] = useState(initialPool?.about ?? "");

  const [price, setPrice] = useState<number | "">(
    initialPool?.price_per_session ?? ""
  );
  const [length, setLength] = useState<number | "">(
    initialPool?.pool_length_m ?? ""
  );
  const [depth, setDepth] = useState(initialPool?.depth_m ?? "");
  const [rating, setRating] = useState<number | "">(
    initialPool?.rating ?? ""
  );
  const [amenities, setAmenities] = useState(
    initialPool?.amenities?.join(", ") ?? ""
  );

  const [isVerified, setIsVerified] = useState(
    initialPool?.is_verified ?? false
  );

  const [status, setStatus] = useState<"active" | "inactive">(
    initialPool?.status ?? "active"
  );

  const [metaTitle, setMetaTitle] = useState(
    initialPool?.meta_title ?? ""
  );
  const [metaDescription, setMetaDescription] = useState(
    initialPool?.meta_description ?? ""
  );
  const [metaKeywords, setMetaKeywords] = useState(
    initialPool?.meta_keywords?.join(", ") ?? ""
  );

  const [imagePreview, setImagePreview] = useState<string | null>(
    initialPool?.image ?? null
  );
  const [imageAlt, setImageAlt] = useState(
    initialPool?.image_alt ?? ""
  );

  // Manual location fields
  const [googleMapUrl, setGoogleMapUrl] = useState(
    initialPool?.googlemap_url ?? ""
  );
  const [lat, setLat] = useState<number | "">(
    initialPool?.lat ?? ""
  );
  const [lng, setLng] = useState<number | "">(
    initialPool?.lng ?? ""
  );

  const [error, setError] = useState<string | null>(null);

  // ----------------------------------
  // IMAGE HANDLER
  // ----------------------------------
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("Image size must be less than 5MB");
      return;
    }

    setError(null);
    setImagePreview(URL.createObjectURL(file));
  };

  // ----------------------------------
  // SUBMIT
  // ----------------------------------
  const handleSubmit = () => {
    setError(null);

    if (!poolname.trim()) return setError("Pool name is required");
    if (!city.trim()) return setError("City is required");
    if (!state.trim()) return setError("State is required");
    if (!type.trim()) return setError("Pool type is required");
    if (!timing.trim()) return setError("Timing is required");
    if (!imagePreview) return setError("Pool image is required");
    if (!imageAlt.trim()) return setError("Image alt text is required");

    if (lat === "" || lng === "")
      return setError("Latitude and longitude are required");

    const pool: Pool = {
      id: initialPool?.id ?? Date.now(),
      poolname,
      city,
      state,
      type,
      timing,
      about: about || undefined,
      image: imagePreview,
      image_alt: imageAlt.trim(),
      lat: Number(lat),
      lng: Number(lng),
      googlemap_url: googleMapUrl || undefined,

      price_per_session:
        price === "" ? undefined : Number(price),
      pool_length_m:
        length === "" ? undefined : Number(length),
      depth_m: depth || undefined,
      rating:
        rating === "" ? undefined : Number(rating),
      amenities: amenities
        ? amenities.split(",").map(a => a.trim()).filter(Boolean)
        : undefined,

      is_verified: isVerified,
      status,

      meta_title:
        metaTitle.trim() ||
        `${poolname} Swimming Pool in ${city}`,
      meta_description:
        metaDescription.trim() ||
        about?.replace(/<[^>]*>/g, "").slice(0, 160),
      meta_keywords: metaKeywords
        ? metaKeywords.split(",").map(k => k.trim()).filter(Boolean)
        : undefined,

      created_at:
        initialPool?.created_at ?? new Date().toISOString(),
      updated_at: new Date().toISOString(),
      views: initialPool?.views ?? 0,
    };

    onSubmit(pool);
  };

  // ----------------------------------
  // UI
  // ----------------------------------
  return (
    <div className="space-y-6">
      {error && (
        <div className="border border-red-300 bg-red-50 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Image */}
      <div>
        <label className="text-sm font-medium">Pool Image</label>
        <div className="relative border-2 border-dashed rounded-xl p-4 bg-muted">
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="absolute inset-0 opacity-0 cursor-pointer"
          />
          {imagePreview ? (
            <img
              src={imagePreview}
              alt={imageAlt || "Pool preview"}
              className="h-44 w-full object-cover rounded-lg"
            />
          ) : (
            <div className="h-44 flex items-center justify-center text-muted-foreground text-sm">
              Click to upload image
            </div>
          )}
        </div>
      </div>

      <input
        placeholder="Image alt text"
        value={imageAlt}
        onChange={e => setImageAlt(e.target.value)}
        className="w-full border rounded-lg px-4 py-2"
      />

      <input
        placeholder="Pool name"
        value={poolname}
        onChange={e => setPoolname(e.target.value)}
        className="w-full border rounded-lg px-4 py-2"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input
          placeholder="City"
          value={city}
          onChange={e => setCity(e.target.value)}
          className="w-full border rounded-lg px-4 py-2"
        />
        <input
          placeholder="State"
          value={state}
          onChange={e => setState(e.target.value)}
          className="w-full border rounded-lg px-4 py-2"
        />
      </div>

      <input
        placeholder="Pool type"
        value={type}
        onChange={e => setType(e.target.value)}
        className="w-full border rounded-lg px-4 py-2"
      />

      <input
        placeholder="Timing (e.g. 6 AM - 10 PM)"
        value={timing}
        onChange={e => setTiming(e.target.value)}
        className="w-full border rounded-lg px-4 py-2"
      />

      <HtmlTextEditor value={about} onChange={setAbout} />

      {/* LOCATION (MANUAL) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <input
          placeholder="Google Maps URL"
          value={googleMapUrl}
          onChange={e => setGoogleMapUrl(e.target.value)}
          className="w-full border rounded-lg px-4 py-2 sm:col-span-3"
        />
        <input
          type="number"
          step="any"
          placeholder="Latitude"
          value={lat}
          onChange={e =>
            setLat(e.target.value === "" ? "" : Number(e.target.value))
          }
          className="w-full border rounded-lg px-4 py-2"
        />
        <input
          type="number"
          step="any"
          placeholder="Longitude"
          value={lng}
          onChange={e =>
            setLng(e.target.value === "" ? "" : Number(e.target.value))
          }
          className="w-full border rounded-lg px-4 py-2"
        />
      </div>

      {/* PRICING */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input
          type="number"
          placeholder="Price per session"
          value={price}
          onChange={e =>
            setPrice(e.target.value === "" ? "" : Number(e.target.value))
          }
          className="w-full border rounded-lg px-4 py-2"
        />
        <input
          type="number"
          placeholder="Pool length (meters)"
          value={length}
          onChange={e =>
            setLength(e.target.value === "" ? "" : Number(e.target.value))
          }
          className="w-full border rounded-lg px-4 py-2"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input
          placeholder="Depth (e.g. 1.5 - 2.5)"
          value={depth}
          onChange={e => setDepth(e.target.value)}
          className="w-full border rounded-lg px-4 py-2"
        />
        <input
          type="number"
          step="0.1"
          placeholder="Rating"
          value={rating}
          onChange={e =>
            setRating(e.target.value === "" ? "" : Number(e.target.value))
          }
          className="w-full border rounded-lg px-4 py-2"
        />
      </div>

      <input
        placeholder="Amenities (comma separated)"
        value={amenities}
        onChange={e => setAmenities(e.target.value)}
        className="w-full border rounded-lg px-4 py-2"
      />

      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={isVerified}
          onChange={e => setIsVerified(e.target.checked)}
        />
        <span className="text-sm">Verified pool</span>
      </div>

      <select
        value={status}
        onChange={e => setStatus(e.target.value as "active" | "inactive")}
        className="w-full border rounded-lg px-4 py-2"
      >
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>

      {/* META */}
      <div className="border rounded-xl p-4 bg-muted space-y-3">
        <input
          placeholder="Meta title"
          value={metaTitle}
          onChange={e => setMetaTitle(e.target.value)}
          className="w-full border rounded-lg px-4 py-2"
        />
        <textarea
          placeholder="Meta description"
          value={metaDescription}
          onChange={e => setMetaDescription(e.target.value)}
          className="w-full border rounded-lg px-4 py-2 min-h-[80px]"
        />
        <input
          placeholder="Meta keywords"
          value={metaKeywords}
          onChange={e => setMetaKeywords(e.target.value)}
          className="w-full border rounded-lg px-4 py-2"
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 border rounded-lg"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="px-5 py-2 bg-blue-600 text-white rounded-lg"
        >
          {loading ? "Saving..." : "Save Pool"}
        </button>
      </div>
    </div>
  );
}

export default AddPoolForm;
